import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isArray } from 'lodash';

import patchTarget from 'Api/target/patchTarget';
import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { targetUpsertEditDomainText } from 'Const/target/text';
import { useTargetState } from 'View/target/state';

import getPatchTargetRequest from './model/request/getPatchTargetRequest';
import { useTranslation } from 'react-i18next';

const usePatchTarget = () => {
  const { t } = useTranslation('target');
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { updateState, resetState, filterState, resetFilterState } = useTargetState();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, formData }) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = getPatchTargetRequest(formData);
      return await patchTarget({ id, payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['target', filterState] });

      onOpenToast({
        status: 'success',
        description: t('domain.successSaveUpdate'),
        duration: 5,
        alwaysShow: false,
      });

      resetState();
      resetFilterState();
    },
    onError: (error) => {
      const errorMessage = isArray(error?.response?.data?.errors) ? error?.response?.data?.errors[0]?.message : targetUpsertEditDomainText?.errorDescription;

      onOpenToast({
        status: 'error',
        description: targetUpsertEditDomainText?.errorDescription,
        duration: 5,
        alwaysShow: false,
      });

      updateState({
        isOpenFormUpsert: false,
        errorFields: {},
        alertProps: {
          isOpen: true,
          type: 'error',
          title: targetUpsertEditDomainText?.errorTitle,
          description: errorMessage,
          onClose: () => updateState({ alertProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePatchTarget;
